import React from 'react';

interface VehicleBuildSpecsProps {
  chassis?: string | null;
  engine?: string | null;
  transmission?: string | null;
  drivetrain?: string | null;
  buildStatus?: string | null;
  title?: string;
  className?: string;
}

const BUILD_STATUS_LABELS: Record<string, string> = {
  planned: 'Planned',
  in_progress: 'In progress',
  complete: 'Complete',
};

const formatBuildStatus = (value?: string | null): string | null => {
  if (!value) return null;
  return BUILD_STATUS_LABELS[value] || value.replace(/_/g, ' ');
};

/**
 * Manufacturer-style spec sheet for build identity fields.
 */
const VehicleBuildSpecs: React.FC<VehicleBuildSpecsProps> = ({
  chassis,
  engine,
  transmission,
  drivetrain,
  buildStatus,
  title = 'Technical specifications',
  className = '',
}) => {
  const items: { label: string; value: string }[] = [];
  if (engine) items.push({ label: 'Engine', value: engine });
  if (transmission) items.push({ label: 'Transmission', value: transmission });
  if (drivetrain) items.push({ label: 'Drivetrain', value: drivetrain });
  if (chassis) items.push({ label: 'Chassis', value: chassis });
  const buildStatusLabel = formatBuildStatus(buildStatus);
  if (buildStatusLabel) items.push({ label: 'Build status', value: buildStatusLabel });

  if (items.length === 0) return null;

  return (
    <section className={className} aria-labelledby="build-specs-heading">
      <div className="border-b border-white/[0.06] pb-3 mb-2">
        <p className="kicker mb-2">Build identity</p>
        {title && (
          <h2 id="build-specs-heading" className="heading-section text-lg sm:text-xl">
            {title}
          </h2>
        )}
      </div>
      <dl className="divide-y divide-white/[0.06]/60">
        {items.map((item) => (
          <div
            key={item.label}
            className="grid grid-cols-1 sm:grid-cols-[minmax(7rem,28%)_1fr] gap-1 sm:gap-4 py-3.5 sm:py-4 sm:items-baseline"
          >
            <dt className="spec-label">{item.label}</dt>
            <dd className="text-sm sm:text-base font-medium text-white leading-snug break-words">
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default VehicleBuildSpecs;
